import { useEffect } from "react";
import type { TimelineBlock } from "../../api/types";
import { usePlayer } from "../../state/player";
import { HIT_SLOP_PX, hit, landsOnLiveEdge, panBy, xToTime, zoomAt } from "../geometry";
import { applyEdge, type SnapKind } from "../snap";
import { type EdgeMutations, commitEdge, pickEdge, resolveEdge } from "./edges";
import type { Landing } from "./landing";
import type { TimelineCtx, TimelineRefs } from "./state";
import type { Tooltip } from "./tooltip";

/** Movement below this is a click, not a drag; a mouse jitters by a pixel or two. */
const DRAG_THRESHOLD_PX = 3;

/** The same, for a finger. A tap wanders further than a mouse click, and a tap that reads as a
 *  drag lands on the centre of the view instead of the instant under the finger. */
const TOUCH_DRAG_THRESHOLD_PX = 10;

export type Cursor = "grab" | "grabbing" | "pointer" | "ew-resize";

/** What one gesture remembers between events, from the first press to the last lift. */
interface GestureState {
  pointerId: number | null;
  startX: number;
  startCenter: number;
  wantsLive: boolean;
  dragThreshold: number;
  /** Every pointer currently down on the canvas, by id, at its last known local x. */
  down: Map<number, number>;
  /** Set once a second finger lands, cleared only when the last one lifts. */
  pinching: boolean;
  pinchDist: number;
  pinchMidX: number;
}

const localX = (canvas: HTMLCanvasElement, event: PointerEvent | WheelEvent) =>
  event.clientX - canvas.getBoundingClientRect().left;

/**
 * The block at an x, ignoring y.
 *
 * Shows and tracks share the row, so the pick is by x alone. Tracks are tried first
 * because they are drawn on top; anything they miss falls through to the show. Ticks are
 * one to a few pixels wide, so the hit area is widened rather than demanding pixel
 * accuracy from a person with a mouse.
 */
function pickAtX(r: TimelineRefs, x: number): TimelineBlock | null {
  const scene = r.scene.current;
  if (!scene) return null;
  return hit(scene.tracks, x, HIT_SLOP_PX)?.item ?? hit(scene.shows, x)?.item ?? null;
}

/** The same, for a pointer: nothing outside the content bands counts as a hit. */
function pick(r: TimelineRefs, x: number, y: number): TimelineBlock | null {
  const scene = r.scene.current;
  if (!scene) return null;
  // The bands that were painted, not the ones a constant says should have been. The two
  // are the same number today and were not always: the canvas is sized from what the data
  // needs, so recomputing from a module constant maps the pointer onto a lane that is not
  // on screen.
  const b = scene.bands;
  if (y < b.contentTop || y > b.contentBottom) return null;
  return pickAtX(r, x);
}

function onPointerDown(g: GestureCtx, s: GestureState, event: PointerEvent): void {
  if (event.button !== 0) return;
  g.canvas.setPointerCapture(event.pointerId);
  s.down.set(event.pointerId, localX(g.canvas, event));
  if (s.down.size === 2) {
    // A second finger supersedes whatever the first was doing: an edge drag half committed
    // and a pan half travelled are both answers to a gesture that turned out to be
    // something else.
    s.pinching = true;
    g.refs.panning.current = false;
    g.refs.draft.current = null;
    g.setSnapNote(null);
    g.refs.aim.current = null;
    const [a, b] = [...s.down.values()];
    s.pinchDist = Math.abs(a! - b!);
    s.pinchMidX = (a! + b!) / 2;
    usePlayer.getState().setFollow(false);
    g.refs.dirty.current = true;
    return;
  }
  // A third finger, and every finger after a pinch has begun: the first two entries in the
  // map keep driving it, and nothing else starts a pan behind them.
  if (s.pinching) return;
  s.dragThreshold = event.pointerType === "touch" ? TOUCH_DRAG_THRESHOLD_PX : DRAG_THRESHOLD_PX;
  s.pointerId = event.pointerId;
  s.startX = localX(g.canvas, event);
  s.startCenter = g.refs.view.current.centerMs;
  g.refs.panning.current = false;
  s.wantsLive = false;
  // A gesture that never delivered its pointerup -- a lost capture, a cancelled touch --
  // would otherwise leave the bar pinned to an aim nothing is going to resolve.
  g.refs.aim.current = null;
  // A new gesture supersedes any landing still resolving, by the same token the two
  // landings already check after every await. Both wait on lookups and both end in
  // `landOn`, which writes the position and turns following back on -- so a click or a
  // flick whose lookups are still in flight when the next press comes down would hand
  // the view and the audio to an instant the viewer has already moved on from, in the
  // middle of the gesture that moved them on.
  g.refs.landing.current++;

  const y = event.clientY - g.canvas.getBoundingClientRect().top;
  const grabbed = pickEdge(g.refs, g.editable, s.startX, y);
  if (grabbed) {
    const block = (g.refs.data.current?.shows ?? []).find((b) => b.id === grabbed.id);
    if (block) {
      const startMs = Date.parse(block.start);
      const endMs = Date.parse(block.end);
      g.refs.draft.current = {
        id: grabbed.id,
        edge: grabbed.edge,
        startMs,
        endMs,
        originalStartMs: startMs,
        originalEndMs: endMs,
        snap: null,
      };
      g.setCursor("ew-resize");
      g.setTooltip(null);
      g.refs.dirty.current = true;
      return;
    }
  }
  g.setCursor("grabbing");
}

/** Two fingers on the glass: zoom about their midpoint and pan with it, one step per move. */
function onPinch(g: GestureCtx, s: GestureState): void {
  if (s.down.size < 2) return;
  const [a, b] = [...s.down.values()];
  const dist = Math.abs(a! - b!);
  const midX = (a! + b!) / 2;
  // Incremental rather than measured from the start of the gesture: the view is clamped
  // at the archive's ends on every step, and a factor derived from the start would keep
  // asking for the zoom the walls already refused. A degenerate spread is a finger
  // landing on top of another.
  //
  // Anchored on the midpoint unconditionally, unlike the wheel: two fingers on the canvas
  // is the viewer taking over, so `setFollow(false)` has already run and there is no
  // pinned playhead to fight.
  if (dist > 0 && s.pinchDist > 0) {
    g.refs.view.current = g.walls.clampView(zoomAt(g.refs.view.current, midX, s.pinchDist / dist));
  }
  g.refs.view.current = g.walls.clampView(panBy(g.refs.view.current, midX - s.pinchMidX));
  s.pinchDist = dist;
  s.pinchMidX = midX;
  g.refs.dirty.current = true;
  g.syncWindow();
}

function onPointerMove(g: GestureCtx, s: GestureState, event: PointerEvent): void {
  const x = localX(g.canvas, event);
  const y = event.clientY - g.canvas.getBoundingClientRect().top;

  if (s.down.has(event.pointerId)) s.down.set(event.pointerId, x);
  if (s.pinching) {
    onPinch(g, s);
    return;
  }

  const draft = g.refs.draft.current;
  if (draft && s.pointerId === event.pointerId && event.buttons & 1) {
    const snapped = resolveEdge(g.refs, xToTime(g.refs.view.current, x), event.altKey, draft.id);
    const span = applyEdge({ startMs: draft.startMs, endMs: draft.endMs }, draft.edge, snapped.ms);
    g.refs.draft.current = { ...draft, ...span, snap: snapped.kind };
    g.setSnapNote(snapped.kind);
    g.refs.dirty.current = true;
    return;
  }

  if (s.pointerId === event.pointerId && event.buttons & 1) {
    const dx = x - s.startX;
    if (!g.refs.panning.current && Math.abs(dx) < s.dragThreshold) return;
    if (!g.refs.panning.current) {
      g.refs.panning.current = true;
      // Dragging is scrubbing, and scrubbing means the viewer has taken over from the
      // playhead. The "return to now" button is how they hand it back.
      usePlayer.getState().setFollow(false);
    }
    const view = g.refs.view.current;
    const desiredCenterMs = s.startCenter - dx * view.msPerPx;
    // Recorded here rather than re-derived on release: the pointer sends no moves while it is
    // held against the stop, so by the time the button comes up the clock has moved on and
    // the clamped centre no longer describes what the gesture asked for.
    const nowMs = Date.now();
    s.wantsLive = landsOnLiveEdge(desiredCenterMs, g.latestMs(), nowMs, g.walls.wallDelayMs());
    g.refs.view.current = g.walls.clampView({ ...view, centerMs: desiredCenterMs });
    // Where the bar is drawn until the landing writes it. The clamped centre rather than
    // the desired one: held against a wall the gesture asks for more than it gets, and the
    // bar belongs where the view actually stopped.
    g.refs.aim.current = g.refs.view.current.centerMs;
    g.refs.dirty.current = true;
    g.syncWindow();
    return;
  }

  // A finger never raises a tooltip that `pointerleave` erases a frame later. A tap
  // already selects the block and the player names it, so nothing becomes unreachable.
  if (event.pointerType === "touch") return;
  const block = pick(g.refs, x, y);
  const id = block?.id ?? null;
  if (id !== g.refs.hover.current) {
    g.refs.hover.current = id;
    g.refs.dirty.current = true;
  }
  const overEdge = pickEdge(g.refs, g.editable, x, y) !== null;
  g.setCursor(overEdge ? "ew-resize" : block ? "pointer" : "grab");
  g.setTooltip(block && !overEdge ? { x, y, block } : null);
}

function onPointerUp(g: GestureCtx, s: GestureState, event: PointerEvent): void {
  s.down.delete(event.pointerId);
  if (s.pinching) {
    // Pinch is the touch wheel, and the wheel does not seek. Held until the last finger
    // lifts so the finger left behind cannot resume a pan from a start position two
    // seconds stale.
    if (s.down.size === 0) {
      s.pinching = false;
      s.pointerId = null;
      g.setCursor("grab");
    }
    return;
  }
  if (s.pointerId !== event.pointerId) return;
  s.pointerId = null;
  g.setCursor("grab");

  // Read and cleared before the landing runs: the render loop derives the selection off
  // the bar again from here, and the bar is still sitting on the aim the gesture came to
  // rest at -- so the border moves once, to what was actually landed on.
  const panned = g.refs.panning.current;
  g.refs.panning.current = false;

  const draft = g.refs.draft.current;
  if (draft) {
    g.refs.draft.current = null;
    g.setSnapNote(null);
    g.refs.dirty.current = true;
    commitEdge(g.mutations, g.t, draft);
    return;
  }
  if (panned) {
    if (s.wantsLive) g.landing.enterLive(true);
    else {
      // What the centre bar came down on, which is what the gesture aimed at. The
      // playhead is pinned there, so the pick is the same one a click makes -- and
      // without it a landing selects nothing and names nothing.
      const view = g.refs.view.current;
      void g.landing.scrubTo(view.centerMs, pickAtX(g.refs, view.width / 2)?.id ?? null);
    }
    return;
  }

  const x = localX(g.canvas, event);
  const y = event.clientY - g.canvas.getBoundingClientRect().top;
  const block = pick(g.refs, x, y);
  void g.landing.activate(block, xToTime(g.refs.view.current, x));
}

function onPointerLeave(g: GestureCtx): void {
  g.refs.hover.current = null;
  g.refs.dirty.current = true;
  g.setTooltip(null);
}

/* A gesture the browser took away -- a scroll it decided to own, a palm on the glass. No
   landing runs: a cancelled gesture asked for nothing. */
function onPointerCancel(g: GestureCtx, s: GestureState, event: PointerEvent): void {
  s.down.delete(event.pointerId);
  if (s.down.size === 0) s.pinching = false;
  if (s.pointerId !== event.pointerId) return;
  s.pointerId = null;
  g.refs.panning.current = false;
  g.refs.draft.current = null;
  g.refs.aim.current = null;
  g.setSnapNote(null);
  g.setCursor("grab");
  g.refs.dirty.current = true;
}

function onWheel(g: GestureCtx, event: WheelEvent): void {
  // Not passive: the page must not scroll while the timeline is being zoomed.
  event.preventDefault();
  const view = g.refs.view.current;
  if (event.shiftKey) {
    const desiredCenterMs = view.centerMs + event.deltaY * view.msPerPx;
    const nowMs = Date.now();
    if (landsOnLiveEdge(desiredCenterMs, g.latestMs(), nowMs, g.walls.wallDelayMs())) {
      g.landing.enterLive(false);
    } else {
      // Which clears atLive too, by the store's own invariant.
      usePlayer.getState().setFollow(false);
    }
    g.refs.view.current = g.walls.clampView({ ...view, centerMs: desiredCenterMs });
  } else {
    // Exponential in the wheel delta, so a fast flick covers the whole zoom range and
    // a slow one still resolves single steps.
    //
    // Anchored on the cursor only while the view is free. With the playhead pinned to the
    // centre the next frame puts the centre back on it, so an anchored centre never renders
    // -- and on the way it would still cost a fetch-window check for a frame that never
    // existed. Zooming must not drop out of following, either: inspecting the live block is
    // exactly what zooming is for, and leaving the live edge here is not a passive act.
    const anchorX = usePlayer.getState().follow ? view.width / 2 : localX(g.canvas, event);
    g.refs.view.current = g.walls.clampView(zoomAt(view, anchorX, Math.exp(event.deltaY * 0.0015)));
  }
  g.refs.dirty.current = true;
  g.syncWindow();
}

/** Everything the pointer layer needs from the component, on top of the shared context. */
export interface GestureDeps extends TimelineCtx {
  landing: Landing;
  mutations: EdgeMutations;
  setCursor: (cursor: Cursor) => void;
  setTooltip: (tooltip: Tooltip | null) => void;
  setSnapNote: (kind: SnapKind | null) => void;
}

/** The same, once the canvas the listeners are attached to is known. */
interface GestureCtx extends GestureDeps {
  canvas: HTMLCanvasElement;
}

export function useTimelineGestures(d: GestureDeps): void {
  useEffect(() => {
    const canvas = d.refs.canvas.current;
    if (!canvas) return;
    const g: GestureCtx = { ...d, canvas };
    const s: GestureState = {
      pointerId: null,
      startX: 0,
      startCenter: 0,
      wantsLive: false,
      dragThreshold: DRAG_THRESHOLD_PX,
      down: new Map(),
      pinching: false,
      pinchDist: 0,
      pinchMidX: 0,
    };
    const onDown = (event: PointerEvent) => onPointerDown(g, s, event);
    const onMove = (event: PointerEvent) => onPointerMove(g, s, event);
    const onUp = (event: PointerEvent) => onPointerUp(g, s, event);
    const onCancel = (event: PointerEvent) => onPointerCancel(g, s, event);
    const onLeave = () => onPointerLeave(g);
    const onScroll = (event: WheelEvent) => onWheel(g, event);

    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointercancel", onCancel);
    canvas.addEventListener("pointerleave", onLeave);
    canvas.addEventListener("wheel", onScroll, { passive: false });
    return () => {
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointercancel", onCancel);
      canvas.removeEventListener("pointerleave", onLeave);
      canvas.removeEventListener("wheel", onScroll);
    };
  }, [d]);
}
